import { Journey } from '@audibene/ta.utils-opportunity'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { AppointmentType, teleaudiologyOrTaLightJourneys, Route, RouteParameters, SearchParameters } from 'constants/'
import { useCountry, useIsSchedulingV2RedirectPending, useProviderRecommendationForAppointmentTypes, useSearchParameters } from 'hooks/'
import { appendSearchParamsToPathname, hydratePathname } from 'util/'

interface Props {
  appointmentType: AppointmentType
  customerSalesforceId: string
  journey?: Journey
  opportunitySalesforceId?: string
}

/**
 * On consultant route redirect to schedule
    - if appointment type feature flag is true and journey is TA

    - override above and do not redirect
        - if preview is true
 */
const useRedirectToProviderRecommendationFlowIfNeeded = ({
  appointmentType,
  customerSalesforceId,
  journey,
  opportunitySalesforceId,
}: Props) => {
  const country = useCountry()
  const navigate = useNavigate()
  const searchParams = useSearchParameters()
  const appointmentTypeFeatureFlagMap = useProviderRecommendationForAppointmentTypes()
  const isV2RedirectPending = useIsSchedulingV2RedirectPending()

  useEffect(() => {
    if (isV2RedirectPending) return

    const {
      [SearchParameters.OPPORTUNITY_GID]: searchParamOpportunitySalesforceId,
      [SearchParameters.JOURNEY]: searchParamJourney,
      [SearchParameters.PREVIEW]: searchParamIsPreview,
    } = searchParams

    const actualJourney = (journey || searchParamJourney) as Journey
    const actualOpportunitySalesforceId = (opportunitySalesforceId || searchParamOpportunitySalesforceId) as string
    const isPreview = searchParamIsPreview?.toString().trim().toLowerCase() === 'true'

    const isProviderRecommendationSupportedAppointmentType = Boolean(appointmentTypeFeatureFlagMap[appointmentType])
    const isProviderRecommendationSupportedJourney = teleaudiologyOrTaLightJourneys.has(actualJourney)
    const isProviderRecommendationFullySupported =
      isProviderRecommendationSupportedAppointmentType && isProviderRecommendationSupportedJourney

    const shouldRedirectToPartnerRecommendationFlow = !isPreview && isProviderRecommendationFullySupported

    if (shouldRedirectToPartnerRecommendationFlow) {
      const pathname = hydratePathname(Route.SCHEDULE, {
        [RouteParameters.COUNTRY]: country,
      })

      const pathnameAndSearch = appendSearchParamsToPathname({
        pathname,
        searchParams: {
          ...searchParams,
          [SearchParameters.APPOINTMENT_TYPE]: appointmentType,
          [SearchParameters.CUSTOMER_GID]: customerSalesforceId,
          [SearchParameters.JOURNEY]: actualJourney,
          [SearchParameters.OPPORTUNITY_GID]: actualOpportunitySalesforceId,
        },
      })

      // Redirect to new schedule page to schedule using provider recommendations
      navigate(pathnameAndSearch)
    }
  }, [
    appointmentType,
    appointmentTypeFeatureFlagMap,
    customerSalesforceId,
    journey,
    opportunitySalesforceId,
    searchParams,
    navigate,
    isV2RedirectPending,
  ])
}

export { useRedirectToProviderRecommendationFlowIfNeeded }
